import { useAuth } from 'react-oidc-context';
import { useLogEvent } from 'api/services/LogEventService';
import ContactButton from 'components/common/ContactButton/ContactButton';
import RequestContactDialog from 'components/common/Requests/RequestContactDialog';
import RequestDocumentDialog from 'components/common/Requests/RequestDocumentDialog';
import RequestQuoteOrSampleDialog from 'components/common/Requests/RequestQuoteOrSampleDialog';
import AddToProjectModal from 'components/common/Search/AddToProjectModal/AddToProjectModal';
import { formatSearchLog } from 'helpers/utilsTS';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RequestContext, RequestTypes } from 'types/ContactRequests';
import { LogEventType } from 'types/LogEvents';
import { ProductType } from 'types/Products';
import {
  doesCompanyRejectInquiries,
  inquiryRejectionTooltipText
} from 'components/pages/products/inquiryRejection';
import { getProductTypeSingulars } from 'api/types/ReferenceData';
import { useIfVerifiedUser } from 'hooks/useUserState_utils';
import { Stack } from '@mui/material';
import { EventSearchDocument } from 'types/Search';
import { IS_UXBEAUTY } from 'components/constants/Constants';
import AttachMoney from '@mui/icons-material/AttachMoney';
import Colorize from '@mui/icons-material/Colorize';
import ChatBubble from '@mui/icons-material/ChatBubble';
import Add from '@mui/icons-material/Add';
import InsertDriveFile from '@mui/icons-material/InsertDriveFile';
import Share from '@mui/icons-material/Share';
import { ShareButton } from 'components/common/ShareButton/ShareButton';
import { isProductOutOfStock } from 'components/pages/products/helpers/formAttributes';
import { SegmentTag } from 'types/global';
import { useParams } from 'react-router-dom';
import { AddToCartDialog } from 'components/common/Requests/AddToCartDialog';
import ShoppingCartOutlined from '@mui/icons-material/ShoppingCartOutlined';

export enum ActionsModalType {
  DOCUMENT_REQUEST = 'documentRequest',
  QUOTE = 'quote',
  SAMPLE = 'sample',
  CONTACT = 'contact',
  PROJECT = 'project',
  CART_SAMPLE = 'CART_SAMPLE'
}
interface ProductsActionsProps {
  products: string[];
  productName: string;

  context: RequestContext; // on the context we should pass if its a project and its id or its an event and its id etc etc
  productsType: ProductType;
  logEventsData: {
    products: EventSearchDocument[];
    searchRequest?: any;
  };
  documents?: any;
  showAddToProject: boolean;
  companyKey?: string;
}

const useSaveClickEvent = () => {
  const logEvent = useLogEvent();

  const saveClickEvent = (
    logEventsData: any,
    eventLog: LogEventType,
    eventKey?: string
  ) => {
    if (logEventsData?.searchRequest !== undefined) {
      const LogProductsFormat = logEventsData?.products?.map((pro: any) => {
        return { document: pro };
      });
      const data = formatSearchLog(
        {
          results: LogProductsFormat,
          search: logEventsData?.searchRequest
        },
        logEventsData?.searchRequest.filters
          ? logEventsData?.searchRequest.filters
          : [],
        SegmentTag.PERSONALCARE
      );

      logEvent(data, eventLog, eventKey);
    }
  };

  return saveClickEvent;
};

export const ProductsActionsSearchHeader = ({
  products,
  productName,
  context,
  productsType,
  showAddToProject,
  logEventsData,
  documents,
  companyKey: companyId
}: ProductsActionsProps) => {
  const [modalType, setModalType] = useState<ActionsModalType | null>();
  const { event: eventKey } = useParams();
  const saveClickEvent = useSaveClickEvent();
  const handleRequestModal = (
    type: ActionsModalType | null,
    eventLog: LogEventType | null
  ) => {
    if (eventLog !== null) {
      saveClickEvent(logEventsData, eventLog, eventKey);
    }
    setModalType(type);
  };
  const { isAuthenticated, isLoading } = useAuth();
  const verified = useIfVerifiedUser();

  const notAuthenticated = isLoading || !isAuthenticated || !verified;
  const { t } = useTranslation();
  if (products.length === 0) return null;

  const companyRejectsInquiries = doesCompanyRejectInquiries(companyId);
  const generalDisabledTooltipText = companyRejectsInquiries
    ? inquiryRejectionTooltipText
    : undefined;

  const outOfStock =
    IS_UXBEAUTY &&
    isProductOutOfStock(
      logEventsData.products[0].event?.product_private_attributes?.data
        ?.inStock?.[0]
    );

  return (
    <>
      <Stack
        direction={'row'}
        sx={{
          flexWrap: 'wrap',
          gap: 1
        }}
      >
        {IS_UXBEAUTY && (
          <ContactButton
            tooltip={
              notAuthenticated
                ? t('actionButtons.request.tooltips.cart_sample_disabled')
                : generalDisabledTooltipText ??
                  t('actionButtons.request.tooltips.cart_sample')
            }
            onClick={() => {
              handleRequestModal(
                ActionsModalType.CART_SAMPLE,
                LogEventType.OPENED_REQUEST_SAMPLE
              );
            }}
            icon={<ShoppingCartOutlined />}
            disabled={companyRejectsInquiries || notAuthenticated}
          />
        )}
        {(productsType === ProductType.INGREDIENTS ||
          productsType === ProductType.FORMULATIONS) && (
          <ContactButton
            tooltip={
              notAuthenticated
                ? t('actionButtons.request.tooltips.disabled', {
                    type: RequestTypes.QUOTE.toLowerCase(),
                    productType:
                      getProductTypeSingulars(productsType).toLowerCase()
                  })
                : generalDisabledTooltipText ??
                  t('actionButtons.request.tooltips.enabled', {
                    type: RequestTypes.QUOTE.toLowerCase(),
                    productType:
                      getProductTypeSingulars(productsType).toLowerCase()
                  })
            }
            onClick={() => {
              handleRequestModal(
                ActionsModalType.QUOTE,
                LogEventType.OPENED_REQUEST_QUOTE
              );
            }}
            icon={<AttachMoney />}
            disabled={companyRejectsInquiries || notAuthenticated}
          />
        )}
        {productsType === ProductType.INGREDIENTS && !IS_UXBEAUTY && (
          <ContactButton
            tooltip={
              notAuthenticated
                ? t('actionButtons.request.tooltips.disabled', {
                    type: RequestTypes.SAMPLE.toLowerCase(),
                    productType:
                      getProductTypeSingulars(productsType).toLowerCase()
                  })
                : outOfStock
                ? t('actionButtons.request.tooltips.outOfStock')
                : generalDisabledTooltipText ??
                  t('actionButtons.request.tooltips.enabled', {
                    type: RequestTypes.SAMPLE.toLowerCase(),
                    productType:
                      getProductTypeSingulars(productsType).toLowerCase()
                  })
            }
            onClick={() => {
              handleRequestModal(
                ActionsModalType.SAMPLE,
                LogEventType.OPENED_REQUEST_SAMPLE
              );
            }}
            icon={<Colorize />}
            disabled={companyRejectsInquiries || notAuthenticated || outOfStock}
          />
        )}
        <ContactButton
          tooltip={
            notAuthenticated
              ? t('actionButtons.contact.tooltips.disabled', {
                  type: RequestTypes.CONTACT.toLowerCase(),
                  productType:
                    getProductTypeSingulars(productsType).toLowerCase()
                })
              : generalDisabledTooltipText ??
                t('actionButtons.contact.tooltips.enabled', {
                  type: RequestTypes.CONTACT.toLowerCase(),
                  productType:
                    getProductTypeSingulars(productsType).toLowerCase()
                })
          }
          onClick={() =>
            handleRequestModal(
              ActionsModalType.CONTACT,
              LogEventType.OPENED_REQUEST_CONTACT
            )
          }
          icon={<ChatBubble />}
          disabled={companyRejectsInquiries || notAuthenticated}
        />
        {(productsType === ProductType.INGREDIENTS ||
          productsType === ProductType.FORMULATIONS) && (
          <ContactButton
            onClick={() =>
              handleRequestModal(
                ActionsModalType.DOCUMENT_REQUEST,
                LogEventType.OPENED_REQUEST_DOCUMENT
              )
            }
            icon={<InsertDriveFile />}
            tooltip={
              notAuthenticated
                ? t('actionButtons.request.tooltips.disabled', {
                    type: RequestTypes.DOCUMENT.toLowerCase(),
                    productType:
                      getProductTypeSingulars(productsType).toLowerCase()
                  })
                : generalDisabledTooltipText ??
                  t('actionButtons.request.tooltips.enabled', {
                    type: RequestTypes.DOCUMENT.toLowerCase(),
                    productType:
                      getProductTypeSingulars(productsType).toLowerCase()
                  })
            }
            disabled={companyRejectsInquiries || notAuthenticated}
          />
        )}
        {showAddToProject && (
          <ContactButton
            disabled={notAuthenticated}
            tooltip={t(
              `actionButtons.addProductToProject.tooltips.${
                notAuthenticated ? 'disabled' : 'enabled'
              }`,
              {
                productType: getProductTypeSingulars(productsType).toLowerCase()
              }
            )}
            onClick={() =>
              handleRequestModal(
                ActionsModalType.PROJECT,
                LogEventType.ADD_PRODUCT_TO_PROJECT
              )
            }
            icon={<Add />}
          />
        )}
        <ShareButton from="product" name={productName}>
          <ContactButton
            disabled={false}
            tooltip={t(`actionButtons.shareContact.tooltips.enabled`)}
            icon={<Share />}
          />
        </ShareButton>
      </Stack>
      {/* // REQUESTS MODALS SECTION */}
      {!notAuthenticated && (
        <>
          {modalType === ActionsModalType.DOCUMENT_REQUEST && (
            <RequestDocumentDialog
              productType={productsType}
              show={true}
              fullProducts={logEventsData.products}
              searchContext={logEventsData.searchRequest !== undefined}
              context={context}
              requestType={RequestTypes.DOCUMENT}
              setModal={handleRequestModal}
              documents={documents}
            />
          )}
          {modalType === ActionsModalType.SAMPLE && (
            <RequestQuoteOrSampleDialog
              productType={productsType}
              show={true}
              context={context}
              requestType={RequestTypes.SAMPLE}
              setModal={handleRequestModal}
              fullProducts={logEventsData.products}
              searchContext={logEventsData.searchRequest !== undefined}
            />
          )}
          {modalType === ActionsModalType.QUOTE && (
            <RequestQuoteOrSampleDialog
              productType={productsType}
              show={true}
              context={context}
              requestType={RequestTypes.QUOTE}
              setModal={handleRequestModal}
              fullProducts={logEventsData.products}
              searchContext={logEventsData.searchRequest !== undefined}
            />
          )}
          {modalType === ActionsModalType.CONTACT && (
            <RequestContactDialog
              show={true}
              setModal={handleRequestModal}
              context={context}
              requestType={RequestTypes.CONTACT}
              fullProducts={logEventsData.products}
              searchContext={logEventsData.searchRequest !== undefined}
              productType={productsType}
            />
          )}

          {modalType === ActionsModalType.PROJECT && (
            <AddToProjectModal
              product={logEventsData.products[0]}
              setShow={handleRequestModal}
              show={true}
            />
          )}
          {modalType === ActionsModalType.CART_SAMPLE && (
            <AddToCartDialog
              open={modalType === ActionsModalType.CART_SAMPLE}
              onClose={() => handleRequestModal(null, null)}
              products={logEventsData.products}
            />
          )}
        </>
      )}
    </>
  );
};
